import { useEffect, useMemo, useState } from 'react';
import { Plus, List, Columns } from 'lucide-react';
import { Link } from 'react-router-dom';
import { AppShell } from '@/components/layout/AppShell';
import { Card } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { Tabs } from '@/components/ui/Tabs';
import { Button } from '@/components/ui/Button';
import { Pagination } from '@/components/ui/Pagination';
import { RoleGate } from '@/components/auth/RoleGate';
import { useRequests } from '@/api/requests';
import { RequestList } from '@/components/requests/RequestList';
import { CreateRequestModal } from '@/components/requests/CreateRequestModal';
import { useAuth } from '@/hooks/useAuth';
import { useAuthStore } from '@/stores/auth';
import { useIsClient } from '@/hooks/useIsClient';
import { usePageTitle } from '@/hooks/usePageTitle';
import type { RequestStatus } from '@/types/api';
import { apiError } from '@/lib/api';

const PAGE_LIMIT = 50;

type TabValue = 'all' | 'open' | 'submitted' | 'under_review' | 'accepted' | 'rejected';

const STATUS_MAP: Record<Exclude<TabValue, 'all'>, RequestStatus> = {
  open: 'open',
  submitted: 'submitted',
  under_review: 'under_review',
  accepted: 'accepted',
  rejected: 'rejected',
};

export function RequestsPage() {
  const isClient = useIsClient();
  usePageTitle(isClient ? 'To-do' : 'Requests');
  const { user, hasRole } = useAuth();
  const activeOrgId = useAuthStore((s) => s.activeOrgId);
  const orgId = activeOrgId ?? user?.organization_id ?? null;
  const isStaff = hasRole(['sysadmin', 'manager', 'staff']);
  const [tab, setTab] = useState<TabValue>('all');
  const [creating, setCreating] = useState(false);
  const [offset, setOffset] = useState(0);

  // Reset pagination when tab/filter changes.
  useEffect(() => {
    setOffset(0);
  }, [tab, orgId]);

  const statusParam = tab === 'all' ? null : STATUS_MAP[tab];
  const { data, isLoading, isError, error } = useRequests({
    org_id: orgId,
    status: statusParam,
    limit: PAGE_LIMIT,
    offset,
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  const emptyAction = useMemo(
    () =>
      isStaff ? (
        <Button
          variant="secondary"
          onClick={() => setCreating(true)}
        >
          <Plus size={14} /> <span className="hidden sm:inline">New request</span>
        </Button>
      ) : undefined,
    [isStaff],
  );
  const emptyDescription = isStaff
    ? undefined
    : "Your assigned BillerBay team hasn't requested anything yet.";

  return (
    <AppShell crumbs={[{ label: isClient ? 'To-do' : 'Requests' }]}>
      <Card
        title={
          <div className="flex items-center gap-3">
            <span>File requests</span>
            <div className="flex items-center gap-0 border border-slate-200 dark:border-[#2A2A4A] bg-white dark:bg-[#1A1A2E]">
              <span className="p-1.5 text-bb-navy dark:text-white bg-slate-100 dark:bg-white/10">
                <List size={14} />
              </span>
              <Link
                to="/requests/board"
                className="p-1.5 text-slate-400 dark:text-[#8888AA] hover:text-bb-navy dark:hover:text-white hover:bg-slate-50 dark:hover:bg-white/10 transition-colors"
                title="Board view"
              >
                <Columns size={14} />
              </Link>
            </div>
          </div>
        }
        actions={
          <RoleGate roles={['sysadmin', 'manager', 'staff']}>
            <Button
              variant="secondary"
              onClick={() => setCreating(true)}
              className="text-xs"
            >
              <Plus size={14} /> <span className="hidden sm:inline">New request</span>
            </Button>
          </RoleGate>
        }
      >
        {isStaff && (
          <p className="text-xs text-slate-500 mb-3">
            Use file requests to ask a client for a specific document with a deadline. They'll see it on their dashboard.
          </p>
        )}
        <Tabs<TabValue>
          value={tab}
          onChange={setTab}
          tabs={[
            { value: 'all', label: 'All' },
            { value: 'open', label: 'Open' },
            { value: 'submitted', label: 'Submitted' },
            { value: 'under_review', label: 'Under review' },
            { value: 'accepted', label: 'Accepted' },
            { value: 'rejected', label: 'Rejected' },
          ]}
          className="mb-4"
        />

        {isLoading ? (
          <div className="flex justify-center py-10">
            <Spinner />
          </div>
        ) : isError ? (
          <div className="text-sm text-red-600">{apiError(error)}</div>
        ) : (
          <>
            <RequestList
              requests={items}
              emptyAction={emptyAction}
              emptyDescription={emptyDescription}
            />
            <Pagination
              total={total}
              limit={PAGE_LIMIT}
              offset={offset}
              onChange={setOffset}
              className="mt-3 border-t border-slate-100 pt-2"
            />
          </>
        )}
      </Card>

      <CreateRequestModal
        open={creating}
        onClose={() => setCreating(false)}
        organizationId={orgId}
      />
    </AppShell>
  );
}
